import {
  DXF_MAPPING_ROLE,
  type DxfEntityKey,
  type DxfEntityRoles,
  type DxfMappingRole,
} from '@frontend/api';
import {
  EMPTY_SELECTION,
  type Selection,
  type Store,
  addToSelection,
  combineStores,
  createLens,
  selectionOf,
} from '@frontend/viewer-core';

import {
  assignmentsEqual,
  entityKeyVisibleSelectionRefs,
  seedAssignments,
  selectionEntityKeys,
} from './mappingModel';
import type {
  DrawingModel,
  DxfSessionState,
  MappingAssignments,
  MappingManager,
  MappingSnapshot,
  MappingState,
  RoleIndex,
} from './types';

export const createMappingSlice = (
  state: Store<DxfSessionState>,
): Store<MappingState> =>
  createLens(
    state,
    (current) => current.mapping,
    (current, mapping) => ({ ...current, mapping }),
  );

/**
 * The unit of undo: what is selected, and what roles are assigned.
 *
 * A **composite** lens, and the one deliberate exception to the one-lens-per-field
 * rule `createDxfViewerSession` describes. It exists because the spec puts both
 * selection and mapping changes on the same undo stack, and an undo therefore has
 * to restore both *in a single write*. Two lenses would mean two `setState` calls
 * and so two notifications, which would leave every subscriber momentarily looking
 * at a half-applied state — new roles against an old selection — and would make
 * the accent overlay rebuild geometry for a frame that should never have existed.
 *
 * One stack over both, rather than one each, is also what makes undoing an
 * assignment restore the selection that produced it, which is what every CAD tool
 * does and what a user expects.
 *
 * Equality is per field, so the snapshot keeps a stable identity while neither has
 * changed — `deriveStore` hands back the previous object — and the history store
 * can compare snapshots by reference.
 */
export const createMappingSnapshotSlice = (
  state: Store<DxfSessionState>,
): Store<MappingSnapshot> =>
  createLens(
    state,
    (current) => ({
      selection: current.selection,
      draft: current.mapping.draft,
    }),
    (current, snapshot) => ({
      ...current,
      selection: snapshot.selection,
      mapping: { ...current.mapping, draft: snapshot.draft },
    }),
    (a, b) => a.selection === b.selection && a.draft === b.draft,
  );

/**
 * Canonical order for a co-applicable pair, so two equal role sets are also
 * `toEqual`-equal and a no-op assignment is detectable by comparing arrays
 * element-wise rather than as sets.
 */
const ROLE_ORDER: readonly DxfMappingRole[] = [
  DXF_MAPPING_ROLE.CUTTING,
  DXF_MAPPING_ROLE.ETCHING,
  DXF_MAPPING_ROLE.BENDING,
  DXF_MAPPING_ROLE.IGNORE,
];

const sortRoles = (roles: readonly DxfMappingRole[]): DxfEntityRoles =>
  [...roles].sort((a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b));

const isSameRoles = (a: DxfEntityRoles, b: DxfEntityRoles): boolean =>
  a.length === b.length && a.every((role, index) => role === b[index]);

/**
 * The roles an entity carries after `role` is applied to it.
 *
 * Exclusive by default: assigning cut, or ignore, replaces whatever was there.
 * Etching and bending are the one co-applicable pair, so applying either to an
 * entity already carrying the other unions them rather than replacing.
 */
const withRole = (
  current: DxfEntityRoles,
  role: DxfMappingRole,
): DxfEntityRoles => {
  const isCoApplicablePair =
    (role === DXF_MAPPING_ROLE.ETCHING &&
      current.includes(DXF_MAPPING_ROLE.BENDING)) ||
    (role === DXF_MAPPING_ROLE.BENDING &&
      current.includes(DXF_MAPPING_ROLE.ETCHING));

  return isCoApplicablePair
    ? sortRoles([DXF_MAPPING_ROLE.ETCHING, DXF_MAPPING_ROLE.BENDING])
    : [role];
};

/**
 * Drops `role` from `current` when at least one role would remain.
 *
 * Assignments must stay non-empty — the write validator rejects an empty set —
 * so removing the last role is a no-op.
 */
const withoutRole = (
  current: DxfEntityRoles,
  role: DxfMappingRole,
): DxfEntityRoles => {
  if (!current.includes(role)) {
    return current;
  }
  const updated = sortRoles(current.filter((entry) => entry !== role));
  return updated.length > 0 ? updated : current;
};

export const createHoveredRoleSlice = (
  state: Store<DxfSessionState>,
): Store<DxfMappingRole | null> =>
  createLens(
    state,
    (current) => current.hoveredRole,
    (current, hoveredRole) => ({ ...current, hoveredRole }),
  );

type Dependencies = {
  mapping: Store<MappingState>;
  /**
   * Selection and draft as one writable unit.
   *
   * Needed by assign-and-clear writes (`assignAndClear`,
   * `toggleRoleOnSelectionAndClear`) so one click costs one undo step.
   */
  snapshot: Store<MappingSnapshot>;
  /** Shared with `selectionController` — selecting a role writes here. */
  selection: Store<Selection>;
  hoveredRole: Store<DxfMappingRole | null>;
  getModel: () => DrawingModel | null;
  getHiddenLayers: () => ReadonlySet<number>;
};

/**
 * The mapping overlay: the saved state, the ephemeral working copy, and the edits
 * that move between them.
 *
 * Every edit produces a new `Map`, which is what lets the history stack hold
 * snapshots as bare references. An edit that would change nothing returns the
 * *same* `Map`, so the lens does not write through — no notification and no
 * history entry for a click that did nothing. Whether there is anything to save
 * is decided by comparing draft to baseline by value (`assignmentsEqual`), not
 * by reference and not by undo depth.
 *
 * Derived values that are a function of *this* slice (`roleIndex`, `counts`) stay
 * methods, matching `SelectionManager.summarize`: the consumer already subscribed
 * to the mapping store, so a second store would only add a subscription to keep
 * in sync. `roleIndex` memoises against the assignments reference because it is
 * rebuilt on every repaint of the role overlay, not only when a person looks at
 * the panel.
 *
 * Derived values that are a function of *this slice and another* — shared roles
 * also depend on the selection — are stores. React cannot call a method and
 * magically subscribe to its inputs; a discarded `useReadableStore` is how that
 * used to be faked, and it is not a supported pattern.
 */
export const createMappingController = ({
  mapping,
  snapshot,
  selection,
  hoveredRole,
  getModel,
  getHiddenLayers,
}: Dependencies): MappingManager => {
  let indexCache: { source: MappingAssignments; index: RoleIndex } | null =
    null;

  const effective = (): MappingAssignments | null => {
    const { draft, saved } = mapping.getState();
    return draft ?? saved;
  };

  const roleIndex = (): RoleIndex => {
    const assignments = effective();
    if (!assignments) {
      return new Map();
    }
    if (indexCache && indexCache.source === assignments) {
      return indexCache.index;
    }

    const previous = indexCache?.index;
    const index = new Map<DxfMappingRole, Set<DxfEntityKey>>();
    assignments.forEach((roles, key) => {
      roles.forEach((role) => {
        const keys = index.get(role);
        if (keys) {
          keys.add(key);
          return;
        }
        index.set(role, new Set([key]));
      });
    });

    // A role whose membership is byte-for-byte the same as last time keeps
    // its previous `Set` reference. Every caller that memoises per role — the
    // role overlay batch builder, the material-fill builder — relies on that
    // reference to tell "this role is untouched" without walking its entities
    // itself; without this, an edit to any one role would hand back four
    // brand-new `Set`s regardless, and every one of them would look changed.
    if (previous) {
      previous.forEach((previousKeys, role) => {
        const nextKeys = index.get(role);
        if (
          nextKeys &&
          nextKeys.size === previousKeys.size &&
          [...nextKeys].every((key) => previousKeys.has(key))
        ) {
          index.set(role, previousKeys);
        }
      });
    }

    indexCache = { source: assignments, index };
    return index;
  };

  /**
   * Applies `role` to `keys`, returning the same map when nothing would change.
   *
   * No entity-type validation here — whether a role legally applies to an
   * entity (bending on a straight line, say) is enforced by a separate backend
   * call, not by the client before it writes the draft.
   */
  const applied = (
    assignments: MappingAssignments,
    keys: Iterable<DxfEntityKey>,
    role: DxfMappingRole,
  ): MappingAssignments => {
    let next: Map<DxfEntityKey, DxfEntityRoles> | null = null;

    for (const key of keys) {
      const current = assignments.get(key) ?? [];
      const updated = withRole(current, role);
      if (isSameRoles(current, updated)) {
        continue;
      }
      next = next ?? new Map(assignments);
      next.set(key, updated);
    }

    return next ?? assignments;
  };

  const removed = (
    assignments: MappingAssignments,
    keys: Iterable<DxfEntityKey>,
    role: DxfMappingRole,
  ): MappingAssignments => {
    let next: Map<DxfEntityKey, DxfEntityRoles> | null = null;

    for (const key of keys) {
      const current = assignments.get(key) ?? [];
      const updated = withoutRole(current, role);
      if (isSameRoles(current, updated)) {
        continue;
      }
      next = next ?? new Map(assignments);
      next.set(key, updated);
    }

    return next ?? assignments;
  };

  const toggledOnSelection = (
    assignments: MappingAssignments,
    keys: ReadonlySet<DxfEntityKey>,
    role: DxfMappingRole,
  ): MappingAssignments => {
    if (keys.size === 0) {
      return assignments;
    }
    const allHaveRole = [...keys].every((key) =>
      (assignments.get(key) ?? []).includes(role),
    );
    return allHaveRole
      ? removed(assignments, keys, role)
      : applied(assignments, keys, role);
  };

  const editDraft = (
    recipe: (
      draft: MappingAssignments,
      model: DrawingModel,
    ) => MappingAssignments,
  ) => {
    const model = getModel();
    if (!model) {
      return;
    }
    mapping.setState((previous) => {
      if (!previous.draft || previous.lockReason !== undefined) {
        return previous;
      }
      const draft = recipe(previous.draft, model);
      return draft === previous.draft ? previous : { ...previous, draft };
    });
  };

  return {
    getState: mapping.getState,
    subscribe: mapping.subscribe,

    loadSaved(assignments) {
      mapping.setState((previous) =>
        previous.saved === assignments
          ? previous
          : { ...previous, saved: assignments },
      );
    },

    setLockReason(lockReason) {
      mapping.setState((previous) =>
        previous.lockReason === lockReason
          ? previous
          : { ...previous, lockReason },
      );
    },

    beginSession() {
      const model = getModel();
      if (!model) {
        return;
      }
      mapping.setState((previous) => {
        if (previous.lockReason !== undefined || previous.draft) {
          return previous;
        }
        // Same reference in both fields, deliberately: it is what makes entering
        // mapping mode read as "no edits yet" rather than as an immediate change.
        const seeded = previous.saved ?? seedAssignments(model);
        return { ...previous, draft: seeded, baseline: seeded };
      });
    },

    endSession() {
      mapping.setState((previous) =>
        previous.draft === null && previous.baseline === null
          ? previous
          : { ...previous, draft: null, baseline: null },
      );
    },

    assign(keys, role) {
      editDraft((draft) => applied(draft, keys, role));
    },

    assignAndClear(keys, role) {
      const model = getModel();
      if (!model || mapping.getState().lockReason !== undefined) {
        return;
      }
      snapshot.setState((previous) => {
        if (!previous.draft) {
          return previous;
        }
        const keyList = [...keys];
        if (keyList.length === 0) {
          return previous;
        }
        const nextDraft = applied(previous.draft, keyList, role);
        if (
          nextDraft === previous.draft &&
          previous.selection === EMPTY_SELECTION
        ) {
          return previous;
        }
        return {
          selection: EMPTY_SELECTION,
          draft: nextDraft,
        };
      });
    },

    assignToSelection(role) {
      editDraft((draft, model) =>
        applied(draft, selectionEntityKeys(selection.getState(), model), role),
      );
    },

    toggleRoleOnSelection(role) {
      editDraft((draft, model) =>
        toggledOnSelection(
          draft,
          selectionEntityKeys(selection.getState(), model),
          role,
        ),
      );
    },

    toggleRoleOnSelectionAndClear(role) {
      const model = getModel();
      if (!model || mapping.getState().lockReason !== undefined) {
        return;
      }
      snapshot.setState((previous) => {
        if (!previous.draft) {
          return previous;
        }
        const keys = selectionEntityKeys(previous.selection, model);
        const nextDraft = toggledOnSelection(previous.draft, keys, role);
        if (nextDraft === previous.draft) {
          return previous.selection === EMPTY_SELECTION
            ? previous
            : { ...previous, selection: EMPTY_SELECTION };
        }
        return {
          selection: EMPTY_SELECTION,
          draft: nextDraft,
        };
      });
    },

    revert() {
      mapping.setState((previous) =>
        previous.draft === previous.baseline
          ? previous
          : { ...previous, draft: previous.baseline },
      );
    },

    commitSaved(assignments) {
      mapping.setState((previous) => ({
        ...previous,
        saved: assignments,
        // Both, so the dirty check comes out false: the draft the user is looking
        // at is now what the server holds.
        draft: previous.draft === null ? null : assignments,
        baseline: previous.draft === null ? null : assignments,
      }));
    },

    selectRole(role, mode = 'replace') {
      const model = getModel();
      const assignments = effective();
      if (!model || !assignments) {
        return;
      }
      // Only visible stitched chunks — same rule as the role overlay and hover
      // preview, so a panel click never selects geometry on a hidden layer.
      const refs = entityKeyVisibleSelectionRefs(
        roleIndex().get(role) ?? [],
        model,
        getHiddenLayers(),
      );
      selection.setState((previous) =>
        mode === 'add' ? addToSelection(previous, refs) : selectionOf(refs),
      );
    },

    setHoveredRole(role) {
      hoveredRole.setState((previous) => (previous === role ? previous : role));
    },

    effective,
    roleIndex,

    isDirty() {
      const { draft, baseline } = mapping.getState();
      if (draft === null || baseline === null) {
        return false;
      }
      return !assignmentsEqual(draft, baseline);
    },

    counts() {
      const counts = new Map<DxfMappingRole, number>();
      roleIndex().forEach((keys, role) => counts.set(role, keys.size));
      return counts;
    },

    selectionSharedRoles: combineStores(
      [mapping, selection],
      (): ReadonlySet<DxfMappingRole> => {
        const model = getModel();
        const assignments = effective();
        if (!model || !assignments) {
          return new Set<DxfMappingRole>();
        }
        const keys = selectionEntityKeys(selection.getState(), model);
        if (keys.size === 0) {
          return new Set<DxfMappingRole>();
        }

        let shared: Set<DxfMappingRole> | null = null;
        for (const key of keys) {
          const roles = assignments.get(key) ?? [];
          if (shared === null) {
            shared = new Set(roles);
            continue;
          }
          for (const role of shared) {
            if (!roles.includes(role)) {
              shared.delete(role);
            }
          }
        }
        return shared ?? new Set();
      },
      (a, b) => a.size === b.size && [...a].every((role) => b.has(role)),
    ),
  };
};
